import { useMemo } from 'react';

import { useCurrentuser } from '@uplift/contexts';
import { isLicenseExpired, licenseRequiresCollaborativeAgreement } from '@uplift/ui-v2';
import { useCollaborativeStatesDetailsQuery, useProviderLicensesCollabAgreementsQuery, useProviderLicensesStatusQuery, useStatesQuery } from '@uplift/queries';

import { PracticeStateRow, PracticeStateStatus } from './components/PracticeStatesTable';
import { CollaborativeStateDetails, License, LicenseCollaborativeAgreement, State } from '@uplift/api';

const deriveStatus = (collaborativeStateDetails: CollaborativeStateDetails, license: License, activeCollabAgreement: LicenseCollaborativeAgreement | undefined): PracticeStateStatus => {
  if (isLicenseExpired(license)) {
    return 'expired';
  }

  if (license.live) {
    return 'live';
  }

  // an existing rejected agreement always surfaces, even if the state details are unavailable
  if (activeCollabAgreement?.rejectedAt != null) {
    return 'rejected';
  }

  if (licenseRequiresCollaborativeAgreement(license, collaborativeStateDetails)) {
    if (!activeCollabAgreement) {
      return 'getStarted';
    }

    return 'inReview';
  }

  return 'goLive';
};

export const usePracticeStatesData = () => {
  const me = useCurrentuser();
  const { data: licenses, isLoading: licensesLoading } = useProviderLicensesStatusQuery(me?.id);
  const { data: states = [], isLoading: statesLoading } = useStatesQuery();
  const { data: collaborativeAgreementsById, isLoading: collabAgreementsLoading } = useProviderLicensesCollabAgreementsQuery(me?.id);
  const { data: collaborativeStateDetails = [] } = useCollaborativeStatesDetailsQuery();

  const stateById = useMemo(
    () => states.reduce((map, state) => ({ ...map, [state.id]: state }), {} as Record<string, State>),
    [ states ]
  );
  const collaborativeStateDetailsById = useMemo(
    () => collaborativeStateDetails.reduce((map, s) => ({ ...map, [s.stateId]: s }), {} as Record<string, CollaborativeStateDetails>),
    [ collaborativeStateDetails ]
  );

  const rows = useMemo<PracticeStateRow[]>(() => {
    if (!licenses) return [];

    return licenses
      .filter(license => !!license.stateId)
      .map((license): PracticeStateRow => {
        const collaborativeStateDetails = collaborativeStateDetailsById[license.stateId];
        const state = stateById[license.stateId];
        const collaborativeAgreements = collaborativeAgreementsById?.[license.id] ?? [];
        const activeCollaborativeAgreement = collaborativeAgreements.find(agreement => agreement.archivedAt === null);
        const requiresCollaborativeAgreement = licenseRequiresCollaborativeAgreement(license, collaborativeStateDetails);

        return {
          id: license.id ?? license.stateId,
          stateId: license.stateId,
          stateName: state?.fullName,
          requiresCollaborativeAgreement: requiresCollaborativeAgreement,
          activeCollaborativeAgreement: activeCollaborativeAgreement,
          licenseExpiration: license.expiration,
          status: deriveStatus(collaborativeStateDetails, license, activeCollaborativeAgreement),
        };
      });
  }, [ licenses, stateById, collaborativeAgreementsById, collaborativeStateDetailsById ]);

  return { rows, isLoading: licensesLoading || statesLoading || collabAgreementsLoading };
};
